import '@/App.css';
import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { DownloadHistoryProvider } from '@/context/DownloadHistoryContext';
import { DemoTourProvider, useDemoTour } from '@/context/DemoTourContext';
import AppShell from '@/components/layout/AppShell';
import SplashPage from '@/pages/SplashPage';
import AccessGate from '@/pages/AccessGate';
import { isAccessGranted } from '@/lib/access';
import OverviewDashboard from '@/pages/OverviewDashboard';
import RiskScore from '@/pages/RiskScore';
import IncidentReview from '@/pages/IncidentReview';
import AIInventory from '@/pages/AIInventory';
import DecisionLedger from '@/pages/DecisionLedger';
import DecisionRecordDetail from '@/pages/DecisionRecordDetail';
import EvidenceVault from '@/pages/EvidenceVault';
import LineageGraph from '@/pages/LineageGraph';
import ComplianceDashboard from '@/pages/ComplianceDashboard';
import AssuranceCenter from '@/pages/AssuranceCenter';
import RegulatoryMomentOfTruth from '@/pages/RegulatoryMomentOfTruth';
import RolesResponsibilities from '@/pages/RolesResponsibilities';
import ExceptionsApprovals from '@/pages/ExceptionsApprovals';
import AgentControls from '@/pages/AgentControls';
import ChangeControl from '@/pages/ChangeControl';
import IntegrationsHub from '@/pages/IntegrationsHub';
import TeamManagement from '@/pages/TeamManagement';
import SettingsPage from '@/pages/SettingsPage';
import ProofChainView from '@/pages/ProofChainView';
import GuidedTour from '@/components/GuidedTour';
import DemoTour from '@/components/DemoTour';
import DemoSelector from '@/pages/demos/DemoSelector';
import CISODemo from '@/pages/demos/CISODemo';
import InvestorDemo from '@/pages/demos/InvestorDemo';
import LinkedInDemo from '@/pages/demos/LinkedInDemo';
import TemporalDemo from '@/pages/demos/TemporalDemo';

function RequireAccess({ children }) {
  const location = useLocation();
  if (!isAccessGranted()) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function RootRoute({ onStartTour }) {
  return isAccessGranted() ? <SplashPage onStartTour={onStartTour} /> : <AccessGate />;
}

function AppInner() {
  const [showTour, setShowTour] = useState(false);
  const { active: demoActive, end: endDemo } = useDemoTour();

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors closeButton />
      {showTour && <GuidedTour onEnd={() => setShowTour(false)} />}
      {demoActive && <DemoTour onEnd={endDemo} />}
      <Routes>
        <Route path="/" element={<RootRoute onStartTour={() => setShowTour(true)} />} />
        <Route path="/demo" element={<RequireAccess><DemoSelector /></RequireAccess>} />
        <Route path="/demo/ciso" element={<RequireAccess><CISODemo /></RequireAccess>} />
        <Route path="/demo/investor" element={<RequireAccess><InvestorDemo /></RequireAccess>} />
        <Route path="/demo/linkedin" element={<RequireAccess><LinkedInDemo /></RequireAccess>} />
        <Route path="/demo/temporal" element={<RequireAccess><TemporalDemo /></RequireAccess>} />
        <Route element={<RequireAccess><AppShell /></RequireAccess>}>
          <Route path="/dashboard" element={<OverviewDashboard />} />
          <Route path="/dashboard/incident-review" element={<IncidentReview />} />
          <Route path="/dashboard/risk-score" element={<RiskScore />} />
          <Route path="/dashboard/ai-inventory" element={<AIInventory />} />
          <Route path="/dashboard/agent-controls" element={<AgentControls />} />
          <Route path="/dashboard/change-control" element={<ChangeControl />} />
          <Route path="/dashboard/decision-ledger" element={<DecisionLedger />} />
          <Route path="/dashboard/decision-ledger/:recordId" element={<DecisionRecordDetail />} />
          <Route path="/dashboard/evidence-vault" element={<EvidenceVault />} />
          <Route path="/dashboard/proof-chain" element={<ProofChainView />} />
          <Route path="/dashboard/lineage" element={<LineageGraph />} />
          <Route path="/dashboard/compliance" element={<ComplianceDashboard />} />
          <Route path="/dashboard/assurance" element={<AssuranceCenter />} />
          <Route path="/dashboard/regulatory-moment" element={<RegulatoryMomentOfTruth />} />
          <Route path="/dashboard/roles" element={<RolesResponsibilities />} />
          <Route path="/dashboard/exceptions" element={<ExceptionsApprovals />} />
          <Route path="/dashboard/integrations" element={<IntegrationsHub />} />
          <Route path="/dashboard/team" element={<TeamManagement />} />
          <Route path="/dashboard/settings" element={<SettingsPage />} />
        </Route>
        {/* Catch-all → gate or splash at / */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <DownloadHistoryProvider>
    <DemoTourProvider>
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
    </DemoTourProvider>
    </DownloadHistoryProvider>
  );
}

export default App;
